import { EntityDefinition } from '@/types/crud';
import { DEFAULT_STATUS_VALUES } from '@/types/enums';

export const kpiEntity: EntityDefinition = {
  name: 'kpi',
  displayName: 'KPIs',
  displayNameSingular: 'KPI',
  apiEndpoint: '/generic/kpis',
  fields: [
    {
      key: 'name',
      label: 'KPI Name',
      type: 'text',
      required: true,
      placeholder: 'Enter KPI name',
      validation: {
        minLength: 2,
        maxLength: 100
      }
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      placeholder: 'Enter KPI description',
      validation: {
        maxLength: 500
      }
    },
    {
      key: 'unit',
      label: 'Unit',
      type: 'text',
      required: true,
      placeholder: 'Enter unit of measurement',
      // Unit is part of the KPI identity. Editing it would change the meaning of stored numbers.
      readOnly: (isEditing: boolean) => isEditing
    },
    {
      key: 'type',
      label: 'Data type',
      type: 'text',
      required: false,
      readOnly: (isEditing: boolean) => isEditing,
      formHidden: false
    },
    {
      key: 'target_type',
      label: 'Direction',
      type: 'text',
      required: false,
      readOnly: (isEditing: boolean) => isEditing
    },
    {
      key: 'frequency',
      label: 'Frequency',
      type: 'select',
      required: false,
      options: [
        { value: 'DAILY', label: 'Daily' },
        { value: 'WEEKLY', label: 'Weekly' },
        { value: 'MONTHLY', label: 'Monthly' },
        { value: 'QUARTERLY', label: 'Quarterly' },
        { value: 'ANNUALLY', label: 'Annually' }
      ],
      defaultValue: 'MONTHLY'
    },
    {
      key: 'tags',
      label: 'Tags',
      type: 'text',
      required: false,
      placeholder: 'Comma-separated labels. Matching ignores case.',
      helpText: 'Discovery labels. Matching ignores case. Not a category taxonomy.'
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      options: [
        { value: 'ACTIVE', label: 'Active' },
        { value: 'INACTIVE', label: 'Inactive' }
      ],
      defaultValue: DEFAULT_STATUS_VALUES.ENTITY
    },
    {
      key: 'yellow_off_pct',
      label: 'Yellow at % off',
      type: 'number',
      required: false,
      formHidden: true
    },
    {
      key: 'red_off_pct',
      label: 'Red at % off',
      type: 'number',
      required: false,
      formHidden: true
    },
    {
      key: 'created_at',
      label: 'Created',
      type: 'date',
      required: false,
      formHidden: true
    }
  ],
  tableColumns: [
    { key: 'name', label: 'KPI Name', sortable: true },
    { key: 'unit', label: 'Unit', sortable: true },
    {
      key: 'tags',
      label: 'Tags',
      sortable: false,
      render: (value: unknown) => {
        const tags = Array.isArray(value)
          ? value
          : typeof value === 'string' && value
            ? value.split(',').map((tag) => tag.trim()).filter(Boolean)
            : [];
        return tags.length ? tags.join(', ') : '—';
      }
    },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'created_at', label: 'Created', sortable: true }
  ],
  searchFields: ['name', 'description', 'tags'],
  defaultSort: { field: 'name', direction: 'asc' },
  deletable: true
};
